import { getOriginChainId } from '@/domain/hooks/useOriginChainId'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { assets } from '@/ui/assets'
import { hyperEVM, hyperTestnet, isolatedRegistryAddress } from './constants'
import { ChainConfigEntry, SupportedChainId } from './types'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { usdxlTokenAddress } from '../contracts-generated'
import { incentivizedPairs, isolatedPartnerPoints } from './points'
import {
  BUDDY_USDXL_PAIR_ADDRESS,
  CMETH_UETH_PAIR_ADDRESS,
  HFUN_USDXL_PAIR_ADDRESS,
  HWHLP_USDHL_PAIR_ADDRESS,
  HWHLP_USDT0_PAIR_ADDRESS,
  WHLP_USDT0_PAIR_ADDRESS,
  LHYPE_USDT0_PAIR_ADDRESS,
  MHYPE_WHYPE_PAIR_ADDRESS,
  PURR_USDXL_PAIR_ADDRESS,
  SUSDE_USDXL_PAIR_ADDRESS,
  USDXL_LHYPE_PAIR_ADDRESS,
  USOL_USDT0_PAIR_ADDRESS,
  XAUT0_USDT0_PAIR_ADDRESS,
} from '../consts'

const commonTokenSymbolToReplacedName = {
  [TokenSymbol('USDC')]: { name: 'Circle USD', symbol: TokenSymbol('USDC') },
  [TokenSymbol('SolvBTC')]: { name: 'SolvBTC', symbol: TokenSymbol('SolvBTC') },
  [TokenSymbol('wstHYPE')]: { name: 'Staked Hype', symbol: TokenSymbol('wstHYPE') },
  [TokenSymbol('WHYPE')]: { name: 'Hype', symbol: TokenSymbol('HYPE') },
  [TokenSymbol('USDe')]: { name: 'USDe', symbol: TokenSymbol('USDe') },
  [TokenSymbol('mHYPE')]: { name: 'Hyperpie HYPE', symbol: TokenSymbol('mHYPE') },
}

const chainConfig: Record<SupportedChainId, ChainConfigEntry> = {
  [hyperTestnet.id]: {
    originChainId: hyperTestnet.id,
    meta: {
      name: 'Hyperliquid EVM Testnet',
      logo: assets.hyperEvmLogo,
    },
    permitSupport: {
      [CheckedAddress('0x2222c34a8dd4ea29743bf8ec4ff165e059839782')]: true, // sUSDe
      [CheckedAddress('0x6fdbaf3102efc67cee53eefa4197be36c8e1a094')]: true, // USDC
      [getContractAddress(usdxlTokenAddress, hyperTestnet.id)]: true, // USDXL
      [CheckedAddress('0x702aB9B6556020F5c7AC814162Cb641d64291144')]: false, // WHYPE
    },
    tokensWithMalformedApprove: [],
    airdrop: {},
    extraTokens: [
      {
        symbol: TokenSymbol('sUSDe'),
        oracleType: 'fixed-usd',
        address: CheckedAddress('0x2222c34a8dd4ea29743bf8ec4ff165e059839782'),
      },
      {
        symbol: TokenSymbol('USDC'),
        oracleType: 'fixed-usd',
        address: CheckedAddress('0x6fdbaf3102efc67cee53eefa4197be36c8e1a094'),
      },
      {
        symbol: TokenSymbol('SolvBTC'),
        oracleType: 'zero-price',
        address: CheckedAddress('0x4B85aCF84b2593D67f6593D18504dBb3A337D3D8'),
      },
      {
        symbol: TokenSymbol('stTESTH'),
        oracleType: 'zero-price',
        address: CheckedAddress('0xe2fbc9cb335a65201fcde55323ae0f4e8a96a616'),
      },
      {
        symbol: TokenSymbol('USDXL'),
        oracleType: 'fixed-usd',
        address: getContractAddress(usdxlTokenAddress, hyperTestnet.id),
      },
      {
        symbol: TokenSymbol('KHYPE'),
        oracleType: 'zero-price',
        address: CheckedAddress('0x2ca0f62dceae63ef25a59f4abfec83264df204c1'),
      },
    ],
    markets: {
      defaultAssetToBorrow: TokenSymbol('USDXL'),
      nativeAssetInfo: {
        nativeAssetName: 'Hype',
        nativeAssetSymbol: TokenSymbol('HYPE'),
        wrappedNativeAssetSymbol: TokenSymbol('WHYPE'),
        wrappedNativeAssetAddress: CheckedAddress('0x702aB9B6556020F5c7AC814162Cb641d64291144'),
        minRemainingNativeAssetBalance: NormalizedUnitNumber(0.001),
      },
      tokenSymbolToReplacedName: {
        ...commonTokenSymbolToReplacedName,
      },
      oracles: {
        [TokenSymbol('WHYPE')]: {
          type: 'market-price',
          providedBy: ['redstone'],
        },
        [TokenSymbol('SolvBTC')]: {
          type: 'market-price',
          providedBy: ['redstone'],
        },
        [TokenSymbol('stTESTH')]: {
          type: 'market-price',
          providedBy: ['redstone'],
        },
        [TokenSymbol('KHYPE')]: {
          type: 'market-price',
          providedBy: ['redstone'],
        },
        [TokenSymbol('USDC')]: {
          type: 'fixed',
        },
        [TokenSymbol('sUSDe')]: {
          type: 'fixed',
        },
        [TokenSymbol('USDXL')]: {
          type: 'fixed',
        },
      },
    },
    isolatedPartnerPoints: {},
    incentivizedPairs: {},
    isolated: {
      registryAddress: isolatedRegistryAddress[hyperTestnet.id],
      oracles: {},
    },
  },
  [hyperEVM.id]: {
    originChainId: hyperEVM.id,
    meta: {
      name: 'Hyperliquid EVM',
      logo: assets.hyperEvmLogo,
    },
    permitSupport: {},
    tokensWithMalformedApprove: [],
    isolatedPartnerPoints,
    incentivizedPairs,
    airdrop: {},
    extraTokens: [
      {
        symbol: TokenSymbol('wstHYPE'),
        oracleType: 'zero-price',
        address: CheckedAddress('0x94e8396e0869c9F2200760aF0621aFd240E1CF38'),
      },
      {
        symbol: TokenSymbol('UBTC'),
        oracleType: 'zero-price',
        address: CheckedAddress('0x9fdbda0a5e284c32744d2f17ee5c74b284993463'),
      },
      {
        symbol: TokenSymbol('USDXL'),
        oracleType: 'fixed-usd',
        address: getContractAddress(usdxlTokenAddress, hyperEVM.id),
      },
      {
        symbol: TokenSymbol('UETH'),
        oracleType: 'zero-price',
        address: CheckedAddress('0xBe6727B535545C67d5cAa73dEa54865B92CF7907'),
      },
      {
        symbol: TokenSymbol('stHYPE'),
        oracleType: 'pyth-oracle',
        oracleAddress: CheckedAddress('0x1d0e4ea616a749c1620118f7d97c111e8ec36e8b'),
        address: CheckedAddress('0xfFaa4a3D97fE9107Cef8a3F48c069F577Ff76cC1'),
      },
    ],
    markets: {
      defaultAssetToBorrow: TokenSymbol('WHYPE'),
      nativeAssetInfo: {
        nativeAssetName: 'Hype',
        nativeAssetSymbol: TokenSymbol('HYPE'),
        wrappedNativeAssetSymbol: TokenSymbol('WHYPE'),
        wrappedNativeAssetAddress: CheckedAddress('0x5555555555555555555555555555555555555555'),
        minRemainingNativeAssetBalance: NormalizedUnitNumber(0.001),
      },
      tokenSymbolToReplacedName: {
        ...commonTokenSymbolToReplacedName,
      },
      oracles: {
        [TokenSymbol('WHYPE')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('wstHYPE')]: {
          type: 'underlying-asset',
          asset: TokenSymbol('wstHYPE'),
        },
        [TokenSymbol('UBTC')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('UETH')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('USDXL')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('USDe')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('feUSD')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('USD₮0')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('USDHL')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
      },
    },
    isolated: {
      registryAddress: isolatedRegistryAddress[hyperEVM.id],
      oracles: {
        [MHYPE_WHYPE_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['pyth'],
        },
        [CMETH_UETH_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['pyth'],
        },
        [PURR_USDXL_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [HFUN_USDXL_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [USDXL_LHYPE_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [SUSDE_USDXL_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [BUDDY_USDXL_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [USOL_USDT0_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [LHYPE_USDT0_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [XAUT0_USDT0_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [HWHLP_USDT0_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['pyth'],
        },
        [HWHLP_USDHL_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['pyth'],
        },
        [WHLP_USDT0_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['pyth'],
        },
      },
    },
  },
}

export function getChainConfigEntry(chainId: number): ChainConfigEntry {
  const originChainId = getOriginChainId(chainId)
  if (originChainId !== chainId) {
    return {
      ...chainConfig[originChainId],
    }
  }

  return chainConfig[chainId]
}
